import { useState } from "react";
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (password: string) => void;
  title?: string;
  description?: string;
  confirmLabel?: string;
  loading?: boolean;
};

const PasswordModal = ({
  open,
  onClose,
  onConfirm,
  title = "Enter Category Password",
  description = "This action requires the category protection password to proceed.",
  confirmLabel = "Confirm",
  loading = false,
}: Props) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const handleConfirm = () => {
    if (!password.trim()) {
      setError("Password is required");
      return;
    }
    setError("");
    onConfirm(password);
  };

  const handleClose = () => {
    setPassword("");
    setError("");
    onClose();
  };

  const style = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 420,
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow:
      "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
    padding: "32px",
    outline: "none",
    border: "1px solid #f1f5f9",
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(15, 23, 42, 0.5)",
          backdropFilter: "blur(4px)",
        },
      }}
    >
      <Box sx={style}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "#dbeafe",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "20px",
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 32, color: "#2563eb" }} />
          </Box>

          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontSize: "18px",
              fontWeight: 700,
              color: "#1e293b",
              marginBottom: "8px",
              letterSpacing: "-0.02em",
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              fontSize: "14px",
              color: "#64748b",
              lineHeight: 1.5,
              marginBottom: "20px",
            }}
          >
            {description}
          </Typography>
        </Box>

        <TextField
          fullWidth
          type="password"
          label="Password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (error) setError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleConfirm();
          }}
          error={Boolean(error)}
          helperText={error}
          autoFocus
          sx={{ marginBottom: "24px" }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "12px",
          }}
        >
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              color: "#64748b",
              borderColor: "#e2e8f0",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              padding: "10px 24px",
              "&:hover": {
                borderColor: "#cbd5e1",
                backgroundColor: "#f8fafc",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleConfirm}
            disabled={loading}
            sx={{
              background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
              color: "#ffffff",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              padding: "10px 24px",
              boxShadow: "0 4px 14px 0 rgba(37, 99, 235, 0.25)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                boxShadow: "0 6px 20px rgba(37, 99, 235, 0.35)",
              },
            }}
          >
            {loading ? "Verifying..." : confirmLabel}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PasswordModal;
